import { ChangeDetectionStrategy, Component, inject, type OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { UiBreadcrumbComponent } from '@big-direkt/ui/breadcrumb';
import { PaginationService, UiPaginationComponent } from '@big-direkt/ui/pagination';
import { UiResultFilterComponent, type UiResultFilterModel } from '@big-direkt/ui/result-filter';
import { ErrorHandlingService } from '@big-direkt/utils/environment';
import { SeoService } from '@big-direkt/utils/seo';
import { NodeTypes, type BreadcrumbItemModel } from '@big-direkt/utils/shared';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { tap } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, shareReplay, switchMap } from 'rxjs/operators';
import { SearchTrackingService } from '../../services/search-tracking.service';
import { SearchService } from '../../services/search.service';
import { ResultComponent } from '../result/result.component';
import { SearchFormComponent } from '../search-form/search-form.component';

const SEARCH_DEBOUNCE_TIME = 20;

@Component({
    selector: 'big-search',
    templateUrl: './search.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ResultComponent,
        SearchFormComponent,
        TranslocoDirective,
        UiBreadcrumbComponent,
        UiPaginationComponent,
        UiResultFilterComponent,
    ],
    host: {
        class: 'block',
    },
})
export class SearchComponent implements OnInit {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly errorHandlingService = inject(ErrorHandlingService);
    private readonly paginationService = inject(PaginationService);
    private readonly searchService = inject(SearchService);
    private readonly seoService = inject(SeoService);
    private readonly translocoService = inject(TranslocoService);
    private readonly searchTrackingService = inject(SearchTrackingService);
    private readonly router = inject(Router);

    public readonly breadcrumb = signal<BreadcrumbItemModel[]>([]);
    public readonly currentPage = this.paginationService.page;
    public readonly filters = toSignal(this.searchService.filters);
    public readonly limit: number = 10;
    public readonly query = toSignal(this.searchService.term);
    public readonly result = toSignal(this.activatedRoute.queryParams.pipe(
        distinctUntilChanged(),
        debounceTime(SEARCH_DEBOUNCE_TIME),
        tap(queryParams => {
            this.breadcrumb.set([
                {
                    uri: this.translocoService.translateObject('search.breadcrumb.frontpage.uri', {}),
                    title: this.translocoService.translateObject('search.breadcrumb.frontpage.title', {}),
                },
                {
                    uri: this.translocoService.translateObject('search.breadcrumb.search.uri', {}),
                    title: this.translocoService.translateObject('search.breadcrumb.search.title', {}),
                },
            ]);

            this.setMetaData(this.translocoService.translateObject('search.pageTitle', { searchTerm: queryParams.q }));
        }),
        switchMap(queryParams => this.searchService.search(
            queryParams.q,
            (this.currentPage() - 1 >= 0 ? this.currentPage() - 1 : 0) * this.limit,
            this.limit,
            queryParams.filter ? JSON.parse(queryParams.filter) : [],
        )),
        catchError(this.errorHandlingService.catchError.bind(this)),
        shareReplay({ refCount: true }),
    ));

    public ngOnInit(): void {
        const currentQueryParams = this.activatedRoute.snapshot.queryParams;

        this.searchService.term.next(currentQueryParams.q);
        this.paginationService.setPage(isNaN(currentQueryParams.page) ? 1 : currentQueryParams.page);
    }

    public onSubmit(): void {
        this.setTrackingInfoState(true);
        this.paginationService.resetPage();
    }

    public onPageChange(page: number): void {
        this.setTrackingInfoState(false);
        this.paginationService.setPage(isNaN(page) ? 1 : page);
    }

    public updateActiveFilter(filter: UiResultFilterModel[]): void {
        this.paginationService.resetPage(PaginationService.defaultOptions, {
            ...this.activatedRoute.snapshot.queryParams,
            filter: JSON.stringify(filter),
        });
        this.setTrackingInfoState(false);
    }

    private setTrackingInfoState(isNewSearchSubmit: boolean): void {
        this.searchTrackingService.trackingInfoState = {
            q: this.activatedRoute.snapshot.queryParams.q,
            src: this.router.url,
            cat: 'search-bar',
            entryType: 'manual_search_entry',
            isNewSearchSubmit,
        };
    }

    private setMetaData(title: string): void {
        this.seoService.initializeNode({
            type: NodeTypes.Search,
            nodeId: 0,
            heading: title,
            pageTitle: undefined,
            metadata: [
                {
                    tag: 'meta',
                    attributes: {
                        name: 'title',
                        content: title,
                    },
                },
            ],
        });
    }
}
